/**
 * HIPAA Package Service Wrapper
 *
 * This file wraps the generated HIPAA package service to provide a cleaner API
 * and handle proper tagging, multipart uploads, and URL construction.
 *
 * The generated service uses full paths with matterId, providerId, and requestId,
 * but we simplify the API here for ease of use.
 */

import {
  enhancedApi as generatedApi,
  HipaaPackageSchema,
} from './generated/hipaaPackageService';

// Re-export types from the generated file
export type {
  ComponentOrderMapping,
  ComponentType,
  ComponentValidationResult,
  CreatePackageComponent,
  CustomUserSchema,
  FieldSource,
  HipaaPackageSchema,
  MatterContextFieldType,
  MissingFieldInfo,
  PackageComponentSchema,
  PackageSource,
  PackageValidationResponse,
  RenderStatus,
  RenderWorkflowResponse,
  ReorderComponentsRequest,
  StaticDocumentTag,
  TemplateSearchResult,
  UpdatePackageComponent,
} from './generated/hipaaPackageService';

// Enhance the generated API with proper tags and simplified endpoints
export const hipaaPackageApi = generatedApi
  .enhanceEndpoints({
    addTagTypes: [
      'hipaa-package',
      'package-component',
      'record-request', // From treatmentRecordRequestService - for 1-to-1 relationship
    ],
    endpoints: {
      // Override tags for better cache invalidation
      requestHipaaPackageRetrieve: {
        providesTags: (result, _error, arg) => {
          if (!result) return [];
          // Handle array or single result
          const packageData = Array.isArray(result) ? result[0] : result;
          return packageData
            ? [
                { type: 'hipaa-package' as const, id: packageData.id },
                {
                  type: 'hipaa-package' as const,
                  id: `REQUEST-${arg.requestId}`,
                },
                { type: 'record-request' as const, id: arg.requestId }, // Link to request
              ]
            : [];
        },
      },
      requestHipaaPackageRetrieve2: {
        providesTags: (result, _error, arg) =>
          result ? [{ type: 'hipaa-package' as const, id: arg.id }] : [],
        // Fix the type since generated returns unknown
        transformResponse: (response: HipaaPackageSchema) => response,
      },
      requestHipaaPackageCreateWithDefaults: {
        invalidatesTags: (_result, _error, arg) => [
          'hipaa-package',
          'package-component',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      },
      requestHipaaPackagePublish: {
        invalidatesTags: (_result, _error, arg) => [
          'hipaa-package',
          { type: 'hipaa-package' as const, id: `REQUEST-${arg.requestId}` },
          'package-component',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      },
      requestHipaaPackageRender: {
        invalidatesTags: (_result, _error, arg) => [
          'hipaa-package',
          { type: 'hipaa-package' as const, id: `REQUEST-${arg.requestId}` },
          'package-component',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      },
      requestHipaaPackageUpload: {
        invalidatesTags: (_result, _error, arg) => [
          'hipaa-package',
          { type: 'hipaa-package' as const, id: `REQUEST-${arg.requestId}` },
          'package-component',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      },
      requestPackageComponentList: {
        providesTags: (result, _error, arg) =>
          result
            ? [
                ...result.map((comp) => ({
                  type: 'package-component' as const,
                  id: comp.id,
                })),
                {
                  type: 'package-component' as const,
                  id: `LIST-${arg.packageId}`,
                },
              ]
            : [],
      },
      requestPackageComponentCreate: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'package-component' as const, id: `LIST-${arg.packageId}` },
          'hipaa-package',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      },
      requestPackageComponentRetrieve: {
        providesTags: (result, _error, arg) =>
          result ? [{ type: 'package-component' as const, id: arg.id }] : [],
      },
      requestPackageComponentUpdate: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'package-component' as const, id: arg.id },
          { type: 'package-component' as const, id: `LIST-${arg.packageId}` },
          'hipaa-package',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      },
      requestPackageComponentDelete: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'package-component' as const, id: `LIST-${arg.packageId}` },
          'hipaa-package',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      },
      requestPackageComponentReorder: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'package-component' as const, id: `LIST-${arg.packageId}` },
          'hipaa-package',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      },
      requestPackageComponentValidateAll: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'package-component' as const, id: `LIST-${arg.packageId}` },
          'hipaa-package',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: (build) => ({
      requestHipaaPackageRetrieve: build.query<
        HipaaPackageSchema,
        {
          matterId: string;
          providerId: string;
          requestId: string;
        }
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/providers/${queryArg.providerId}/record-requests/${queryArg.requestId}/package/`,
          method: 'GET',
        }),
        transformResponse: (response: HipaaPackageSchema) =>
          Array.isArray(response) ? response[0] : response,
        providesTags: (result, _error, arg) =>
          result
            ? [
                {
                  type: 'hipaa-package' as const,
                  id: Array.isArray(result) ? result[0].id : result.id,
                },
                {
                  type: 'hipaa-package' as const,
                  id: `REQUEST-${arg.requestId}`,
                },
                { type: 'record-request' as const, id: arg.requestId },
              ]
            : [],
      }),
      requestHipaaPackageUpload: build.mutation<
        HipaaPackageSchema,
        {
          body: {
            blob_id: string;
          };
          matterId: string;
          providerId: string;
          requestId: string;
        }
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/providers/${queryArg.providerId}/record-requests/${queryArg.requestId}/package/upload/`,
          method: 'POST',
          body: queryArg.body,
        }),
        invalidatesTags: (_result, _error, arg) => [
          'hipaa-package',
          { type: 'hipaa-package' as const, id: `REQUEST-${arg.requestId}` },
          'package-component',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      }),
      requestHipaaPackageRebuildPdf: build.mutation<
        HipaaPackageSchema,
        {
          matterId: string;
          providerId: string;
          requestId: string;
        }
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/providers/${queryArg.providerId}/record-requests/${queryArg.requestId}/package/rebuild-pdf/`,
          method: 'POST',
        }),
        invalidatesTags: (_result, _error, arg) => [
          'hipaa-package',
          { type: 'hipaa-package' as const, id: `REQUEST-${arg.requestId}` },
          'package-component',
          { type: 'record-request' as const, id: arg.requestId },
        ],
      }),
    }),
  });

// Export the enhanced hooks with simpler names
export const {
  // Package hooks
  useRequestHipaaPackageRetrieveQuery,
  useRequestHipaaPackageRetrieve2Query,
  useRequestHipaaPackageCreateWithDefaultsMutation,
  useRequestHipaaPackagePublishMutation,
  useRequestHipaaPackageRenderMutation,
  useRequestHipaaPackageUploadMutation,
  useRequestHipaaPackageRebuildPdfMutation,

  // Component hooks
  useRequestPackageComponentListQuery,
  useRequestPackageComponentCreateMutation,
  useRequestPackageComponentRetrieveQuery,
  useRequestPackageComponentUpdateMutation,
  useRequestPackageComponentDeleteMutation,
  useRequestPackageComponentReorderMutation,
  useRequestPackageComponentValidateAllMutation,
  useRequestPackageComponentSearchTemplatesQuery,
  useLazyRequestPackageComponentSearchTemplatesQuery,
} = hipaaPackageApi;

// Create simplified hook aliases for common use cases
export const useGetPackageQuery = useRequestHipaaPackageRetrieveQuery;
export const useGetPackageByIdQuery = useRequestHipaaPackageRetrieve2Query;

// Additional alias for backward compatibility
export const useGetPackageForRequestQuery = useRequestHipaaPackageRetrieveQuery;
export const useCreatePackageMutation =
  useRequestHipaaPackageCreateWithDefaultsMutation;
export const useCreatePackageWithDefaultsMutation =
  useRequestHipaaPackageCreateWithDefaultsMutation;
export const usePublishPackageMutation = useRequestHipaaPackagePublishMutation;
export const useRenderPackageMutation = useRequestHipaaPackageRenderMutation;
export const useUploadPackageMutation = useRequestHipaaPackageUploadMutation;
export const useRebuildPackagePdfMutation =
  useRequestHipaaPackageRebuildPdfMutation;

export const useGetComponentsQuery = useRequestPackageComponentListQuery;
export const useAddComponentMutation = useRequestPackageComponentCreateMutation;
export const useGetComponentQuery = useRequestPackageComponentRetrieveQuery;
export const useUpdateComponentMutation =
  useRequestPackageComponentUpdateMutation;
export const useRemoveComponentMutation =
  useRequestPackageComponentDeleteMutation;
export const useReorderComponentsMutation =
  useRequestPackageComponentReorderMutation;
export const useValidateAllComponentsMutation =
  useRequestPackageComponentValidateAllMutation;

// Template search hooks
export const useSearchTemplatesQuery =
  useRequestPackageComponentSearchTemplatesQuery;
export const useLazySearchTemplatesQuery =
  useLazyRequestPackageComponentSearchTemplatesQuery;

/**
 * Helper to extract matter, provider, and request IDs from common props
 */
export interface PackageRequestInfo {
  matterId: string;
  providerId: string;
  requestId: string;
}

/**
 * Helper to build component request args
 */
export const buildComponentArgs = (
  info: PackageRequestInfo,
  componentId?: string,
) => ({
  matterId: info.matterId,
  providerId: info.providerId,
  requestId: info.requestId,
  ...(componentId && { id: componentId }),
});
